// Drives the Committee shell's single content <iframe> WITHOUT polluting the
// browser's joint session history.
//
// The parent shell (CommitteeCockpitLive) owns ALL navigation history: every
// public /committee/... URL is a top-window history entry, and the frame is a
// puppet that only ever mirrors @frame_path. Binding `src={@frame_path}` and
// letting morphdom patch the attribute would perform a *real* iframe navigation
// on every change — each one adding a joint-history entry and reloading the
// frame even when it is already showing that URL. Instead the iframe is marked
// `phx-update="ignore"` (LiveView never touches it after the initial render) and
// the server pushes `committee_drive_frame` with the desired path; this hook
// drives it via `contentWindow.location.replace`, which never adds a history
// entry. Combined with the frame-side bridge (which converts the frame's own
// pushState to replaceState), Back/Forward map 1:1 to the parent's entries.
import {normalizePath} from "./committee_frame_preload.js"

const CommitteeResourceFrame = {
  mounted() {
    this.lastDriven = null
    // The page script may have started this frame's first navigation before
    // the shell connected (committee_frame_preload.js). Until that document
    // arrives the frame still reads about:blank, which would look like a lost
    // document and restart the load — so the hook adopts it instead.
    this.preloading = this.el.__committeeFramePreload || null
    // The shell owns the resolved theme and outer drawer state. Reflect them
    // into the same-origin AI OS editor without remounting or losing its draft.
    this.syncEditorLayout = () => {
      if (!document.documentElement.classList.contains("aios-document")) return
      try {
        const root = this.el.contentDocument?.documentElement
        if (root?.classList.contains("aios-document")) {
          root.dataset.aiosMonicaOpen = String(document.documentElement.classList.contains("monica-drawer-open"))
          root.classList.toggle("dark", document.documentElement.classList.contains("dark"))
        }
      } catch (_error) { /* The frame may be between documents. */ }
    }
    if (document.documentElement.classList.contains("aios-document")) {
      this.layoutObserver = new MutationObserver(this.syncEditorLayout)
      this.layoutObserver.observe(document.documentElement, {attributes: true, attributeFilter: ["class"]})
    }

    this.drive = (path, {force = false} = {}) => {
      if (!path) return
      if (typeof document !== "undefined" && document.documentElement?.classList?.contains("aios-document") && path.startsWith("/committee")) {
        path = path.replace(/^\/committee(?=\/|\?|$)/, "/aios")
      }

      let target
      try {
        target = new URL(path, window.location.origin)
      } catch (_error) {
        return
      }
      // Never let the shell drive its content frame off-origin.
      if (target.origin !== window.location.origin) return

      const normalizedTarget = normalizePath(target.href)
      if (!normalizedTarget) return

      // The frame may have navigated ITSELF here (its own push_navigate, which
      // the parent then mirrored back as this drive). Reading the live location
      // avoids a redundant reload in that case. Same-origin, so this won't throw
      // in practice; guard anyway for the mid-load window.
      let currentHref
      try {
        currentHref = this.el.contentWindow.location.href
      } catch (_error) {
        currentHref = null
      }

      // This iframe has no `src`: it only ever holds a document because we put
      // one there. Anything that RELOADS the element — and moving an iframe in
      // the DOM does exactly that, which is what happens when a conditional
      // sibling enters or leaves — drops it back to `about:blank`. In that
      // state `lastDriven` is a lie, so it must not short-circuit us, or the
      // shell shows a blank frame forever.
      const lostDocument = !currentHref || currentHref === "about:blank"

      if (!force && this.preloading && this.preloading === normalizedTarget) {
        this.lastDriven = normalizedTarget
        return
      }
      this.preloading = null

      // Already the last path we drove (or the initial load) — nothing to do.
      // `force` is the Overview's "Try again": same path, fresh document.
      if (!force && !lostDocument && this.lastDriven === normalizedTarget) return

      if (!force && !lostDocument && normalizePath(currentHref) === normalizedTarget) {
        this.lastDriven = normalizedTarget
        return
      }

      this.beginNavigation()
      try {
        // replace() (not assignment) so the frame swap never adds a joint
        // session-history entry — the parent already owns the history entry.
        this.el.contentWindow.location.replace(target.href)
      } catch (_error) {
        this.el.src = target.href
      }
      this.lastDriven = normalizedTarget
    }

    // AI OS: while the next document loads, the current one stays on screen
    // (dimmed) AND the shell keeps its chrome. The server flips
    // `data-fullscreen` / `data-topbar-hidden` in the same patch that moves the
    // frame, so without this the rail and band vanished first and the editor
    // appeared a beat later. `data-aios-frame-loading` lives on <html>, which
    // LiveView never patches; CSS only applies the chrome-hiding rules without
    // it. Cleared on the frame's `load` (or a safety timeout).
    this.beginNavigation = () => {
      if (!document.documentElement.classList.contains("aios-document")) return
      clearTimeout(this.navigationTimer)
      document.documentElement.dataset.aiosFrameLoading = "true"
      this.el.dataset.navigating = "true"
      this.navigationTimer = setTimeout(this.endNavigation, 8000)
    }
    this.endNavigation = () => {
      clearTimeout(this.navigationTimer)
      delete document.documentElement.dataset.aiosFrameLoading
      delete this.el.dataset.navigating
    }

    // Primary mechanism: `data-frame-path` is the current frame URL, patched by
    // LiveView whenever @frame_path changes (rail clicks, frame self-navs the
    // parent mirrored, browser Back — all go through handle_params). mounted()
    // performs the initial load; updated() re-drives on every change.
    // Rede de segurança: um iframe recarregado por movimentação no DOM dispara
    // `load` em `about:blank` sem que a LiveView patcheie nada — nenhum
    // `updated()` chega. Só o próprio elemento sabe que perdeu o documento.
    this.onFrameLoad = () => {
      this.syncEditorLayout()
      let href
      try {
        href = this.el.contentWindow.location.href
      } catch (_error) {
        href = null
      }

      if (!href || href === "about:blank") {
        this.lastDriven = null
        this.preloading = null
        this.drive(this.el.getAttribute("data-frame-path"))
      } else {
        this.preloading = null
        this.endNavigation()
      }
    }

    this.el.addEventListener("load", this.onFrameLoad)

    this.drive(this.el.getAttribute("data-frame-path"))
    this.syncEditorLayout()

    // Secondary: an explicit push_event re-assert for the case where the parent
    // short-circuits handle_params (top URL already correct) but still wants to
    // reassert the frame in case its own navigation drifted.
    this.handleEvent("committee_drive_frame", ({path, force}) =>
      this.drive(path, {force: force === true}),
    )

    // Shell → hosted Overview App (the gear asks the App for its editor). Only
    // while this frame IS the Overview; the frame's bridge (app.js) relays the
    // message to the App it hosts and to nothing else.
    this.handleEvent("committee_home_message", message => {
      if (this.el.dataset.frameKind !== "home") return
      try {
        this.el.contentWindow?.postMessage(
          {type: "seiva:committee:home-message", message},
          window.location.origin,
        )
      } catch (_error) {
        // Frame mid-navigation: the gear can be pressed again.
      }
    })
  },

  updated() {
    this.drive(this.el.getAttribute("data-frame-path"))
    this.syncEditorLayout()
  },

  destroyed() {
    this.endNavigation?.()
    this.el.removeEventListener("load", this.onFrameLoad)
    this.layoutObserver?.disconnect()
  },
}

export default CommitteeResourceFrame
